import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ListParameters, PlanV2 } from 'src/app/core/models';
import { WeeklyReportV2 } from 'src/app/core/models/v2/weekly-report.v2.model';
import { WeeklyReportsV2Service } from 'src/app/core/services/weekly-reports.v2.service';
import Swal from 'sweetalert2';
import { ChartComponent } from '../chart/chart.component';
import { ComunicationService } from 'src/app/pages/medical/bip/services/comunication.service';
import { loadingModal, modalError, modalSuccess, modalWarning } from '../../utils/modal';

@Component({
  selector: 'app-parent-chart',
  templateUrl: './parent-chart.component.html',
  styleUrls: ['./parent-chart.component.scss']
})
export class ParentChartComponent implements OnInit, OnChanges{

  dataGraphic!:WeeklyReportV2[];
  @Input() maladaptive!: PlanV2;
  @Input() isFinalComponent = false;

  @Output() imagesGenerated = new EventEmitter<string[]>(); // Emitir imágenes al padre
  static allChartImages: string[] = [];// Arreglo para almacenar las imágenes generadas

  // Reset the static array when a new PDF generation starts
  static resetChartImages() {
    ParentChartComponent.allChartImages = [];
    console.log('Reset static chart images array');
  }

  formTableGraphic = new FormGroup({
    datos: new FormArray([])
  });

  formAddWeeklyReport = new FormGroup({
    plan_id: new FormControl<number | null>(null, Validators.required),
    week_start: new FormControl<string | null>('', Validators.required),
    week_end: new FormControl<string | null>('', Validators.required),
    value: new FormControl<number | null>(null, Validators.required)
  });

  @ViewChild(ChartComponent) chart!:ChartComponent;
  public showGraphic = false;

  private weeklyReportV2Service = inject(WeeklyReportsV2Service);
  private comunicationService = inject(ComunicationService);

  ngOnInit(): void {
    this.getValues();
    this.comunicationService.updateData$.subscribe(dates => {
      this.getValues(dates.date_from, dates.date_to);
    });
  }

  constructor(){}

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['maladaptive']){
      // console.log('maladaptives: ',this.maladaptive);
      this.formAddWeeklyReport.setValue({
        plan_id: this.maladaptive.id,
        week_start: '',
        week_end: '',
        value: null
      });
    }
  }


  private getValues(date_from: string | null = null, date_to: string | null = null){
    this.showGraphic=false;
    this.dataGraphic = [];
      const params: ListParameters = {
      plan_id: this.maladaptive.id,
      per_page: 500,
    }
    if(date_from)
      params['date_start'] = date_from
    if(date_to)
      params['date_end'] = date_to

    this.dataGraphic = [];
    this.weeklyReportV2Service.list(params).subscribe(
      {
        next:(response) => {
          this.dataGraphic = response.data;
          this.datosArray.clear();

          this.dataGraphic.forEach(element => {
            this.datosArray.push(
              new FormGroup({
                week_end: new FormControl({value: this.formatDate(element.week_end), disabled:true}),
                value: new FormControl(element.value, [Validators.required, Validators.min(0)]),
                week_start: new FormControl(new Date(element.week_start).toISOString().split('T')[0]),
                plan_id: new FormControl(element.plan_id),
                id: new FormControl(element.id)
              })
            );
          });
          this.showGraphic = true;
          const interval = setInterval(() => {
            if(this.chart) {
              this.chart.reloadGraphic();
              clearInterval(interval);
            }
          }, 50);
          if (this.isFinalComponent) {
            setTimeout(() => {
              Swal.close();
            }, 1000);
          }
        },
        error: (error) => {
          console.log('error: ',error)
        }
      }
    );
  }

  addValue(){
    if(this.formAddWeeklyReport.valid){
      loadingModal();

      this.weeklyReportV2Service.create(this.formAddWeeklyReport.value as never).subscribe(
        {
          next: (response) => {
            modalSuccess('Add Weekly Report', response.message ?? 'Weekly report added successfully');
          },
          error: (error) => {
            console.log('error: ',error);
            modalError('Error',error.error.message);
          },
          complete: () => {
            this.getValues();
          }
        }
      );
    }
    else{
      // error, completar campos obligatorios
      modalWarning(
        'Required Fields Missing',
        'You must fill out all required fields before proceeding.'
      );
    }
  }

  onDateChange(event: { value?: Date | string | null }){
    if (!event.value) {
      this.week_end.setValue(null);
      this.week_start.setValue(null);
      return;
    }

    const selectedDate = new Date(event.value);
    this.week_end.setValue(selectedDate.toISOString().split('T')[0]);

    // establecer week_start en 6 dias antes
    const date = new Date(selectedDate);
    date.setDate(date.getDate() -6);
    this.week_start.setValue(date.toISOString().split('T')[0]);
  }

  updateValue(index:number){
    const datoActualizado = this.datosArray.at(index).getRawValue();

    // abrir modal cargando...
    loadingModal('Data Update');

    // modifico formato de week_end
    datoActualizado.week_end = new Date(datoActualizado.week_end).toISOString().split('T')[0];

    // llamo al servicio para actualizar registro
    this.weeklyReportV2Service.update(datoActualizado, datoActualizado.id).subscribe(
      {
        next: (response) => {
          if(response.status === 'success'){
            modalSuccess('Data Update',response.message ?? 'Weekly report updated successfully');
            this.getValues();
            if (this.chart) {
              this.chart.reloadGraphic();
            }
          }
        },
        error: (error) => {
          console.log('error: ',error);
          modalError('Error',error.error.message);
        }
      }
    );

    // end
  }

  async deleteValue(index:number){
    const dataDelete = this.datosArray.at(index).getRawValue();

    Swal.fire({
      title: 'Are you sure you want to delete this weekly report',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    })
    .then((result) => {
      if (result.isConfirmed) {
        // abrir modal cargando...
        loadingModal('Data Delete');

        this.weeklyReportV2Service.delete(dataDelete.id).subscribe(
          {
            next: (response) => {
              modalSuccess('Delete Weekly Report', response.message ?? 'Weekly report deleted successfully');
              this.getValues();
              if (this.chart) {
                this.chart.reloadGraphic();
              }
            },
            error: (error) => {
              console.log('error: ',error);
              modalError('Error',error.error.message);
            }
          }
        );
      }
    });


  }

  get datosArray() {
    return this.formTableGraphic.get('datos') as FormArray;
  }

  get week_end () {
    return this.formAddWeeklyReport.get('week_end') as FormControl;
  }

  get week_start(){
    return this.formAddWeeklyReport.get('week_start') as FormControl;
  }

  private formatDate(date: string | Date){
    const dateFormat = new Date(date).toISOString().split('T')[0];

    const [year, month, day] = dateFormat.split('-');

    return `${month}-${day}-${year}`; //retorna fecha en formato mm-dd-yyyy
  }

  handleImagesGenerated(images: string[]) {
    // Verificar si el arreglo ya tiene las imágenes necesarias (por ejemplo, 3 imágenes)
    const maxImages = 160; // Número máximo de imágenes que deseas almacenar

    if (ParentChartComponent.allChartImages.length >= maxImages) {
      console.log('El arreglo ya está lleno. No se agregarán más imágenes.');
      return; // Salir si ya está lleno
    }
      ParentChartComponent.allChartImages = [...ParentChartComponent.allChartImages, ...images];
    // Emitir el arreglo actualizado al componente padre
    this.imagesGenerated.emit(ParentChartComponent.allChartImages);
  }

  /**
   * Captures the chart image on demand
   * This method is called when the PDF generation is triggered
   * @returns Promise<string[]> Array with the chart image as base64 string
   */
  async captureChartImage(): Promise<string[]> {
    console.log(`🎯 ParentChartComponent.captureChartImage() for goal: ${this.maladaptive?.name || 'Unknown'}`);

    // If chart is not available, return empty array
    if (!this.chart) {
      console.warn(`❌ Chart component not available for goal: ${this.maladaptive?.name || 'Unknown'}`);
      return [];
    }

    // If maladaptive data is not available, return empty array
    if (!this.maladaptive) {
      console.warn('❌ Maladaptive data not available for chart capture');
      return [];
    }

    console.log(`📊 Chart data available for ${this.maladaptive.name}: ${this.dataGraphic?.length || 0} data points`);

    try {
      // Get the chart image as base64 string
      const imgURI = await this.chart.captureChartImage();
      if (imgURI) {
        return [imgURI];
      } else {
        console.warn(`⚠️ Empty image captured for ${this.maladaptive.name}`);
        return [];
      }
    } catch (error) {
      console.error(`❌ Error capturing chart image for ${this.maladaptive.name}:`, error);
      return [];
    }
  }

}
